<template>
  <teleport to="body">
    <transition name="toast-slide">
      <div v-if="visible" class="cart-toast" role="alert" aria-live="polite">

        <!-- Hoofdrij: backorder -->
        <div v-if="isBackorder" class="ct-top ct-top--backorder">
          <div class="ct-check ct-check--backorder"><i class="bi bi-clock-history"></i></div>
          <div class="ct-info">
            <p class="ct-label">Nabestelling geplaatst</p>
            <p class="ct-name">{{ product?.name }}</p>
            <p class="ct-backorder-note">We contacteren je zodra dit product beschikbaar is.</p>
          </div>
          <button class="ct-close" @click="dismiss" aria-label="Sluiten">
            <i class="bi bi-x"></i>
          </button>
        </div>

        <!-- Hoofdrij: normaal toegevoegd -->
        <div v-else class="ct-top">
          <div class="ct-check"><i class="bi bi-check-lg"></i></div>
          <div class="ct-info">
            <p class="ct-label">Toegevoegd aan winkelwagen</p>
            <p class="ct-name">{{ product?.name }}</p>
          </div>
          <button class="ct-close" @click="dismiss" aria-label="Sluiten">
            <i class="bi bi-x"></i>
          </button>
        </div>

        <!-- Alternatief beschikbaar -->
        <div v-if="alternative" class="ct-alt">
          <div class="ct-alt-body">
            <div class="ct-alt-img">
              <img v-if="alternative.images?.[0]?.src" :src="alternative.images[0].src" :alt="alternative.name" loading="lazy">
              <i v-else class="bi bi-ev-station"></i>
            </div>
            <div class="ct-alt-info">
              <p class="ct-alt-label">Goedkoper alternatief beschikbaar</p>
              <p class="ct-alt-name">{{ alternative.name }}</p>
              <p v-if="savings" class="ct-alt-saving">Bespaar {{ savings }}</p>
            </div>
          </div>
          <div class="ct-alt-actions">
            <button class="ct-alt-accept" @click="acceptAlternative" :disabled="swapping">
              <span v-if="swapping"><i class="bi bi-arrow-repeat spin"></i> Wisselen...</span>
              <span v-else><i class="bi bi-arrow-left-right"></i> Neem dit alternatief</span>
            </button>
            <button class="ct-alt-dismiss" @click="dismissAlt">Nee, houd origineel</button>
          </div>
        </div>

        <!-- Acties -->
        <div class="ct-actions">
          <router-link to="/winkelwagen" class="ct-btn-cart" @click="dismiss">
            <i class="bi bi-bag"></i> Bekijk winkelwagen
          </router-link>
          <router-link to="/checkout" class="ct-btn-checkout" @click="dismiss">
            Afrekenen <i class="bi bi-arrow-right"></i>
          </router-link>
        </div>

        <!-- Timer bar -->
        <div class="ct-progress" :style="{ width: progress + '%' }"></div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, watch, computed, onUnmounted } from 'vue'
import { useCartStore } from '@stores/cart'

const cart    = useCartStore()
const visible = ref(false)
const product = ref(null)
const alternative = ref(null)
const altDismissed = ref(false)
const swapping = ref(false)
const progress = ref(100)
const isBackorder = ref(false)

const DURATION = 8000  // ms zichtbaar
let   timer    = null
let   progInt  = null

function formatPrice(p) {
  const n = parseFloat(p)
  if (isNaN(n)) return ''
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n)
}

const savings = computed(() => {
  if (!product.value || !alternative.value) return null
  const orig = parseFloat(product.value.price || product.value.regular_price || 0)
  const alt  = parseFloat(alternative.value.price || alternative.value.regular_price || 0)
  if (!orig || !alt || alt >= orig) return null
  return formatPrice(orig - alt)
})

async function fetchAlternative(sku) {
  if (!sku) return
  try {
    const res = await fetch(`/api/alternatives/${encodeURIComponent(sku)}`)
    if (!res.ok) return
    const data = await res.json()
    const first = (data.products || [])[0]
    if (first && first.id !== product.value?.id) {
      alternative.value = first
    }
  } catch { /* stil */ }
}

function startTimer() {
  clearTimeout(timer)
  clearInterval(progInt)
  progress.value = 100
  const step = 100 / (DURATION / 50)
  progInt = setInterval(() => {
    progress.value = Math.max(0, progress.value - step)
  }, 50)
  timer = setTimeout(dismiss, DURATION)
}

function dismiss() {
  visible.value = false
  alternative.value = null
  altDismissed.value = false
  clearTimeout(timer)
  clearInterval(progInt)
}

function dismissAlt() {
  altDismissed.value = true
  alternative.value = null
}

async function acceptAlternative() {
  if (!alternative.value || !product.value) return
  swapping.value = true
  try {
    // Verwijder origineel, voeg alternatief toe
    cart.removeFromCart(product.value.id)
    await cart.addToCart(alternative.value, 1)
    dismiss()
  } catch (e) {
    console.error('[CartToast] swap mislukt', e)
  } finally {
    swapping.value = false
  }
}

// Luister op lastAdded uit de cart store
watch(() => cart.lastAdded, (val) => {
  if (!val) return
  product.value = val.product
  alternative.value = null
  altDismissed.value = false
  isBackorder.value = !!val.product?._backorder
  visible.value = true
  startTimer()

  // Haal alternatief ALLEEN op bij normale toevoeging (niet bij nabestelling)
  if (!isBackorder.value && val.product?.sku) fetchAlternative(val.product.sku)
})

onUnmounted(() => {
  clearTimeout(timer)
  clearInterval(progInt)
})
</script>

<style scoped>
.cart-toast {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 9999;
  width: min(380px, calc(100vw - 2rem));
  background: #fff;
  border: 1.5px solid #e0e0e0;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.14);
  overflow: hidden;
}

/* ── Header ── */
.ct-top {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 1rem 0.75rem;
}
.ct-check {
  width: 1.75rem;
  height: 1.75rem;
  min-width: 1.75rem;
  border-radius: 50%;
  background: #22c55e;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  margin-top: 0.1rem;
}
.ct-info { flex: 1; min-width: 0; }
.ct-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #22c55e;
  margin-bottom: 0.1rem;
}
.ct-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ct-check--backorder { background: #6b7280; }
.ct-top--backorder .ct-label { color: #6b7280; }
.ct-backorder-note {
  font-size: 0.72rem;
  color: #888;
  margin-top: 0.2rem;
  line-height: 1.4;
}
.ct-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #aaa;
  font-size: 1.1rem;
  line-height: 1;
  padding: 0;
  transition: color .12s;
}
.ct-close:hover { color: #111; }

/* ── Alternatief ── */
.ct-alt {
  margin: 0 0.875rem 0.75rem;
  background: #fffbeb;
  border: 1.5px solid #FFD500;
  border-radius: 10px;
  padding: 0.75rem;
}
.ct-alt-body {
  display: flex;
  gap: 0.625rem;
  margin-bottom: 0.75rem;
}
.ct-alt-img {
  width: 3rem;
  height: 3rem;
  min-width: 3rem;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: 1.25rem;
  color: #ccc;
}
.ct-alt-img img { width: 100%; height: 100%; object-fit: contain; padding: 0.25rem; }
.ct-alt-info { flex: 1; min-width: 0; }
.ct-alt-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #b45309;
  margin-bottom: 0.15rem;
}
.ct-alt-name {
  font-size: 0.8rem;
  font-weight: 700;
  color: #111;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.ct-alt-saving {
  font-size: 0.75rem;
  font-weight: 800;
  color: #15803d;
  margin-top: 0.15rem;
}
.ct-alt-actions { display: flex; flex-direction: column; gap: 0.4rem; }
.ct-alt-accept {
  width: 100%;
  background: #FFD500;
  border: none;
  border-radius: 8px;
  padding: 0.55rem 1rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #111;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: opacity .12s;
}
.ct-alt-accept:hover:not(:disabled) { opacity: .85; }
.ct-alt-accept:disabled { opacity: .6; cursor: default; }
.ct-alt-dismiss {
  background: none;
  border: none;
  font-size: 0.75rem;
  color: #888;
  cursor: pointer;
  text-align: center;
  padding: 0.2rem;
  transition: color .12s;
}
.ct-alt-dismiss:hover { color: #111; }

/* ── Acties ── */
.ct-actions {
  display: flex;
  gap: 0.5rem;
  padding: 0 0.875rem 0.875rem;
}
.ct-btn-cart, .ct-btn-checkout {
  flex: 1;
  text-align: center;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 700;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  transition: opacity .12s;
}
.ct-btn-cart {
  background: #f5f5f5;
  color: #111;
  border: 1.5px solid #e0e0e0;
}
.ct-btn-cart:hover { background: #ebebeb; }
.ct-btn-checkout {
  background: #111;
  color: #fff;
}
.ct-btn-checkout:hover { opacity: .85; }

/* ── Voortgangsbalk ── */
.ct-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: #FFD500;
  transition: width .05s linear;
}

/* ── Transitie ── */
.toast-slide-enter-active,
.toast-slide-leave-active { transition: transform .25s ease, opacity .25s ease; }
.toast-slide-enter-from,
.toast-slide-leave-to    { transform: translateY(1.5rem); opacity: 0; }

.spin { animation: spin .7s linear infinite; display: inline-block; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
